import React, { useState } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Tabs,
  Tab,
  Container,
  alpha,
} from '@mui/material';
import lojTheme, { LOJ_COLORS } from './theme/lojTheme';
import SiteHeader from './components/SiteHeader';
import CrystalClashPanel from './components/CrystalClashPanel';
import SandseaConquestPanel from './components/SandseaConquestPanel';
import TrialCagePanel from './components/TrialCagePanel';
import { crystalClashData, sandseaConquestData } from './data/strategies';
import { LanguageProvider } from './i18n/LanguageContext';
import { useTranslation } from './i18n/LanguageContext';
import LanguageSelector from './components/LanguageSelector';

function AppShell() {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();

  return (
    <ThemeProvider theme={lojTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: `
            radial-gradient(ellipse 120% 60% at 50% 0%, ${alpha(LOJ_COLORS.goldDim, 0.08)} 0%, transparent 60%),
            linear-gradient(180deg, ${LOJ_COLORS.void} 0%, ${LOJ_COLORS.abyss} 100%)
          `,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'fixed',
            inset: 0,
            backgroundImage: `
              repeating-linear-gradient(
                0deg,
                transparent,
                transparent 2px,
                ${alpha(LOJ_COLORS.void, 0.03)} 2px,
                ${alpha(LOJ_COLORS.void, 0.03)} 4px
              )
            `,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 8 }}>
          <Box sx={{ position: 'absolute', top: { xs: 12, md: 20 }, insetInlineEnd: { xs: 12, md: 24 }, zIndex: 20 }}>
            <LanguageSelector />
          </Box>
          <SiteHeader />

          {/* Tab Navigation */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              background: `linear-gradient(180deg, ${alpha(LOJ_COLORS.abyss, 0.98)}, ${alpha(LOJ_COLORS.shadow, 0.98)})`,
              backdropFilter: 'blur(12px)',
              borderBottom: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.2)}`,
              borderTop: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.1)}`,
              mb: 1,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_e, val) => setActiveTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              centered={false}
              sx={{
                '& .MuiTabs-scroller': {
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'center' },
                },
                '& .MuiTabs-flexContainer': {
                  margin: { md: '0 auto' },
                },
                '& .MuiTabs-scrollButtons.Mui-disabled': {
                  opacity: 0.2,
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 1 } }}>
                    <span>🔮</span>
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{t('Crystal Clash')}</Box>
                  </Box>
                }
                sx={{
                  px: { xs: 1.75, sm: 3, md: 6 },
                  py: { xs: 1.4, md: 2 },
                  minWidth: { xs: 0, sm: 90 },
                  fontSize: { xs: '0.72rem', sm: '0.85rem', md: '0.95rem' },
                  '&.Mui-selected': { color: LOJ_COLORS.crystalLight },
                  '&:hover': { color: alpha(LOJ_COLORS.crystalLight, 0.85) },
                }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 1 } }}>
                    <span>🏜️</span>
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{t('Sandsea Conquest')}</Box>
                  </Box>
                }
                sx={{
                  px: { xs: 1.75, sm: 3, md: 6 },
                  py: { xs: 1.4, md: 2 },
                  minWidth: { xs: 0, sm: 90 },
                  fontSize: { xs: '0.72rem', sm: '0.85rem', md: '0.95rem' },
                  '&.Mui-selected': { color: LOJ_COLORS.sandLight },
                  '&:hover': { color: alpha(LOJ_COLORS.sandLight, 0.85) },
                }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.6, sm: 1 } }}>
                    <span>⚔️</span>
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{t('Trial Cage')}</Box>
                  </Box>
                }
                sx={{
                  px: { xs: 1.75, sm: 3, md: 6 },
                  py: { xs: 1.4, md: 2 },
                  minWidth: { xs: 0, sm: 90 },
                  fontSize: { xs: '0.72rem', sm: '0.85rem', md: '0.95rem' },
                  '&.Mui-selected': { color: LOJ_COLORS.ember },
                  '&:hover': { color: alpha(LOJ_COLORS.ember, 0.85) },
                }}
              />
            </Tabs>
            <Box
              sx={{
                height: '2px',
                background: activeTab === 0
                  ? `linear-gradient(to right, transparent, ${LOJ_COLORS.crystalLight}, transparent)`
                  : activeTab === 1
                    ? `linear-gradient(to right, transparent, ${LOJ_COLORS.sandLight}, transparent)`
                    : `linear-gradient(to right, transparent, ${LOJ_COLORS.ember}, transparent)`,
                opacity: 0.3,
              }}
            />
          </Box>

          {/* Crystal Clash */}
          <CrystalClashPanel strategy={crystalClashData} hidden={activeTab !== 0} />

          {/* Sandsea Conquest */}
          <SandseaConquestPanel strategy={sandseaConquestData} hidden={activeTab !== 1} />

          {/* Trial Cage */}
          <TrialCagePanel hidden={activeTab !== 2} />
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            py: 3,
            px: 2,
            borderTop: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.15)}`,
            background: alpha(LOJ_COLORS.void, 0.6),
          }}
        >
          <Box sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.7rem', letterSpacing: '0.2em', color: LOJ_COLORS.goldDim }}>
            ⚜ &nbsp; {t('(EWU) UnitedWeStand · Lands of Jail · Alliance War Council')} &nbsp; ⚜
          </Box>
          <Box sx={{ mt: 0.5, fontFamily: "'Crimson Text', Georgia, serif", fontSize: '0.85rem', color: alpha(LOJ_COLORS.parchmentDark, 0.5), fontStyle: 'italic' }}>
            {t('For alliance eyes only. Distribute within ranks.')}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppShell />
    </LanguageProvider>
  );
}

export default App;
